<template>
  <div class="chatbot-settings">
    <popup
      :title="$t('view.settings.popup.deleteLocale.title')"
      ref="localeDelDialog"
    >
      <p>
        {{ $t("view.settings.popup.deleteLocale.text1") }}
        <b>{{
          chatbot.locales.toDelete
            ? `${chatbot.locales.toDelete.name} (${
                chatbot.locales.toDelete.locale
              })`
            : ""
        }}</b>
        {{ $t("view.settings.popup.deleteLocale.text2") }}
      </p>
      <template v-slot:buttons>
        <btn icon="times" @click="$refs.localeDelDialog.close()">{{
          $t("btn.cancel")
        }}</btn>
        <btn icon="trash" @click="deleteLocale" color="red">{{
          $t("btn.delete")
        }}</btn>
      </template>
    </popup>
    <section-title>{{
      $t("view.settings.chatbot.chatbotTitle")
    }}</section-title>
    <section-title subsection>{{
      $t("view.settings.chatbot.localesTitle")
    }}</section-title>
    <div
      class="chatbot-settings__section settings__basic-section editor-section"
    >
      <div class="editor-section-description">
        {{ $t("view.settings.chatbot.localesDescription") }}
      </div>
      <div class="editor-section settings__chatbot">
        <div class="chatbot-settings__default-locale">
          <sel
            class="chatbot-settings__default-locale-select"
            v-model="chatbot.locales.default"
            :label="$t('view.settings.chatbot.defaultLocale')"
          >
            <option
              v-for="locale in $store.state.flowLocales"
              :key="locale.id"
              :value="locale.id"
              >{{ locale.name }}</option
            >
          </sel>
          <btn
            small
            class="chatbot-settings__def-locale-save-button"
            icon="save"
            @click="saveDefaultLocale"
            >{{ $t("btn.save") }}</btn
          >
        </div>
        <div class="chatbot-settings__all-locales">
          <h4>{{ $t("view.settings.chatbot.allLocalesTitle") }}</h4>
          <div
            class="chatbot-settings__locale-row"
            v-for="locale in this.$store.state.flowLocales"
            :key="locale.id"
          >
            {{ locale.name }} ({{ locale.locale }})
            <fai
              class="chatbot-settings__locale-delete-icon"
              icon="trash"
              @click="
                chatbot.locales.toDelete = locale;
                $refs.localeDelDialog.open();
              "
            ></fai>
          </div>
          <div
            class="chatbot-settings__new-locale"
            v-show="chatbot.locales.creator.show"
          >
            <in
              class="chatbot-settings__new-locale-input"
              :label="$t('view.settings.chatbot.localeNameLabel')"
              v-model="chatbot.locales.creator.name"
            ></in>
            <in
              class="chatbot-settings__new-locale-input"
              :label="$t('view.settings.chatbot.localeLocaleLabel')"
              v-model="chatbot.locales.creator.locale"
            ></in>
            <btn
              class="chatbot-settings__new-locale-btn"
              @click="addLocale"
              icon="save"
            ></btn>
          </div>
          <div
            class="chatbot-settings__locale-add"
            :class="
              chatbot.locales.creator.show
                ? 'chatbot-settings__locale-add--active'
                : ''
            "
          >
            <div
              class="chatbot-settings__locale-add-icon"
              @click="
                chatbot.locales.creator.show = !chatbot.locales.creator.show
              "
            >
              <fai icon="plus"></fai>
            </div>
          </div>
        </div>
      </div>
    </div>
    <section-title subsection>{{
      $t("view.settings.chatbot.moderatorKeywordsTitle")
    }}</section-title>
    <div
      class="chatbot-settings__section settings__basic-section editor-section"
    >
      <div class="editor-section-description">
        {{ $t("view.settings.chatbot.moderatorKeywordsDescription") }}
      </div>
      <div class="editor-section settings__chatbot">
        <h4 class="chatbot-settings__keyword-type-title">
          {{ $t("view.settings.chatbot.moderatorKeywordsModEnable") }}
        </h4>
        <mod-keywords-list
          :operation="1"
          :list="chatbot.modKeywords.enableModChat"
          @add="addModKeyword"
          @delete="deleteModKeyword"
          ref="enableChatModList"
        ></mod-keywords-list>
        <h4 class="chatbot-settings__keyword-type-title">
          {{ $t("view.settings.chatbot.moderatorKeywordsModDisable") }}
        </h4>
        <mod-keywords-list
          :operation="2"
          :list="chatbot.modKeywords.disableModChat"
          @add="addModKeyword"
          @delete="deleteModKeyword"
          ref="disableChatModList"
        ></mod-keywords-list>
      </div>
    </div>
  </div>
</template>

<script>
import store from "@/store";
import axios from "@/axios";

export default {
  data() {
    return {
      chatbot: {
        locales: {
          default: null,
          creator: {
            show: false,
            name: "",
            locale: ""
          },
          toDelete: {}
        },
        modKeywords: {
          enableModChat: [],
          disableModChat: []
        }
      }
    };
  },

  async mounted() {
    try {
      await this.$store.dispatch("loadFlowLocales", this);
      await this.loadModKeywords();
      const defLocale = this.$store.getters.defaultFlowLocale;
      this.chatbot.locales.default = defLocale.id;
    } catch (e) {
      this.notify("error", "view.settings.error.load");
      console.error(e);
    }
  },

  methods: {
    async addLocale() {
      try {
        const createReq = await axios.post("/api/system/locale", {
          name: this.chatbot.locales.creator.name,
          locale: this.chatbot.locales.creator.locale
        });
        this.$store.commit("ADD_LOCALE", createReq.data.created);
        this.chatbot.locales.creator.show = false;
        this.chatbot.locales.creator.name = "";
        this.chatbot.locales.creator.locale = "";

        this.notify("success", "view.settings.success.localeCreate");
      } catch (e) {
        console.error(e);
        this.notify("error", "view.settings.error.localeCreate");
      }
    },

    async deleteLocale() {
      try {
        const deleteReq = await axios.delete("/api/system/locale", {
          data: {
            id: this.chatbot.locales.toDelete.id
          }
        });
        this.$store.commit(
          "DELETE_LOCALE",
          this.chatbot.locales.toDelete.locale
        );
        this.$refs.localeDelDialog.close();
        this.notify("success", "view.settings.success.localeDelete");
      } catch (e) {
        console.error(e);
        this.notify("error", "view.settings.error.localeDelete");
      }
    },

    async saveDefaultLocale() {
      try {
        await axios.put("api/system/default-locale", {
          id: this.chatbot.locales.default
        });
        this.notify("success", "view.settings.success.defaultLocale");
      } catch (e) {
        console.error(e);
        this.notify("error", "view.settings.error.defaultLocale");
      }
    },

    async loadModKeywords() {
      try {
        const data = await axios.get("/api/system/mod-keywords");
        const modEnable = [];
        const modDisable = [];

        for (const row of data.data.list) {
          if (row.operation === 1) modEnable.push(row);
          else if (row.operation === 2) modDisable.push(row);
        }

        this.$set(this.chatbot.modKeywords, "enableModChat", modEnable);
        this.$set(this.chatbot.modKeywords, "disableModChat", modDisable);
      } catch (e) {
        console.error(e);
        throw e;
      }
    },

    async addModKeyword(data) {
      try {
        await axios.post("/api/system/mod-keywords", data);
        if (data.operation === 1) {
          const arr = this.chatbot.modKeywords.enableModChat;
          this.$refs.enableChatModList.clear();
          arr.push(data);
          this.$set(this.chatbot.modKeywords, "enableModChat", arr);
        } else if (data.operation === 2) {
          const arr = this.chatbot.modKeywords.disableModChat;
          this.$refs.disableChatModList.clear();
          arr.push(data);
          this.$set(this.chatbot.modKeywords, "disableModChat", arr);
        }
        this.notify("success", "view.settings.success.keywordsSave");
      } catch (e) {
        console.error(e);
        this.notify("error", "view.settings.error.keywordsSave");
      }
    },

    async deleteModKeyword(operation, id) {
      try {
        await axios.delete('/api/system/mod-keywords/' + id)

        const prop = operation === 1 ? "enableModChat" : "disableModChat";
        const arr = this.chatbot.modKeywords[prop];
        for (const i in arr) {
          if (arr[i].id === id) {
            arr.splice(i, 1);
            break;
          }
        }
        this.$set(this.chatbot.modKeywords, prop, arr);
        this.notify("success", "view.settings.success.keywordsSave");
      } catch (e) {
        console.error(e);
        this.notify("error", "view.settings.error.keywordsSave");
      }
    }
  }
};
</script>

<style lang="scss">
.chatbot-settings {
  &__section {
    margin-bottom: 80px;
    position: relative;
    width: 34vw;
    min-width: 540px;
  }

  &__basic-section {
    display: flex;
    justify-content: center;
    flex-direction: column;
  }

  &__default-locale {
    @include alignedFlex(flex-start);
    align-items: flex-end;
    width: 70%;
    margin-bottom: 10px;
  }

  &__def-locale-save-button {
    margin-left: 25px;
  }

  &__default-locale-select {
    width: 160px;
  }

  &__all-locales {
    h4 {
      margin-bottom: 10px;
    }
  }

  &__locale-row {
    @include alignedFlex(space-between);
    margin-top: 3px;
    width: 200px;
  }

  &__locale-delete-icon {
    margin-left: 10px;
    cursor: pointer;
  }

  &__locale-add {
    margin-top: 10px;
    margin-left: 5px;
    cursor: pointer;

    &--active {
      .settings__locale-add-icon {
        transform: rotate(45deg);
      }
    }
  }

  &__locale-add-icon {
    display: inline-block;
    transition: transform 0.2s ease;
    will-change: transform;
  }

  &__new-locale {
    margin-top: 20px;
  }

  &__new-locale-input {
    width: 22%;
    display: inline-block;
    margin-right: 10px;
  }

  &__keyword-type-title {
    margin-bottom: 10px;
  }
}
</style>
